<template>
    <CCard>
        <CCardHeader>
            <h5 class="my-2" v-translate>Pica Code releases</h5>

            <div class="card-header-actions" v-if="hasPicacodeReleases && canChange">
                <PButton class="btn btn-primary btn-sm" :loading="loading" @click="save">
                    <translate>Save</translate>
                </PButton>
            </div>
        </CCardHeader>
        <CCardBody>
            <table class="table custom-table custom-table--clean" v-if="hasPicacodeReleases">
                <thead>
                <tr>
                    <th scope="col" v-if="showPicacodeReleaseId">#</th>
                    <th scope="col">
                        <translate>Description</translate>
                    </th>
                    <th scope="col">
                        <translate>Quantity</translate>
                    </th>
                    <th scope="col">
                        <translate>From - To</translate>
                    </th>
                    <th scope="col">
                        <translate>Price list</translate>
                    </th>
                    <th scope="col" v-if="canViewCodec">
                        <translate>Codec</translate>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(picacodeRelease, index) in picacodeReleases" :key="index">
                    <td v-if="showPicacodeReleaseId">{{ picacodeRelease.id }}</td>
                    <td>{{ picacodeRelease.description }}</td>
                    <td>{{ picacodeRelease.quantity }}</td>
                    <td>{{ picacodeRelease.first_race_number }} - {{ picacodeRelease.last_race_number }}</td>
                    <td>
                        <PriceListSelect :items="priceLists"
                                         :value="picacodeRelease.price_list"
                                         :placeholder="$gettext('Use default price list')"
                                         :can-change="canChange"
                                         @input="setPicacodeReleaseField(picacodeRelease.id, 'priceList', $event)"
                                         @remove="setPicacodeReleaseField(picacodeRelease.id, 'priceList', null)"
                        />
                        <!-- v-model="picacodeRelease.price_list" :add-promo-item="true"-->
                    </td>
                    <td v-if="canViewCodec">
                        <CodecSelect :items="codecs"
                                     :value="picacodeRelease.codec"
                                     :placeholder="$gettext('Use default codec')"
                                     :can-change="canChange"
                                     @input="setPicacodeReleaseField(picacodeRelease.id, 'codec', $event)"
                                     @remove="setPicacodeReleaseField(picacodeRelease.id, 'codec', null)"
                        />
                    </td>
                </tr>
                </tbody>
            </table>

            <div v-else>
                <CAlert show color="light" class="d-flex justify-content-between align-items-center p-0">
                    <translate>No Pica Codes have been released yet</translate>

                    <router-link :to="{'name': 'picacodeReleaseList'}" v-if="hasPerm('pica.add_picacoderelease')">
                        <CButton color="primary" size="sm">
                            <translate>Generate Pica Code</translate>
                        </CButton>
                    </router-link>
                </CAlert>
            </div>
        </CCardBody>
    </CCard>
</template>

<script>
import PicacodeReleaseService from "@/services/picacoderelease.service"
import PriceListSelect from "@/components/PriceListSelect"
import CodecSelect from "@/components/CodecSelect"
import PButton from "@/components/PButton"

export default {
    name: "PicacodeReleases",

    components: {
        PriceListSelect,
        CodecSelect,
        PButton
    },

    props: {
        picaServiceId: {
            type: Number,
            required: true
        },

        priceLists: {
            type: Array
        },

        codecs: {
            type: Array
        },

        showPicacodeReleaseId: {
            type: Boolean,
            default: true
        },

        canChange: {
            type: Boolean,
            default: true
        },
        canViewCodec: {
            type: Boolean,
            default: true
        },
    },

    data() {
        return {
            picacodeReleases: null,
            picacodeReleaseFields: [],
            loading: false
        }
    },

    mounted() {
        PicacodeReleaseService.getPicacodeReleases(this.picaServiceId)
            .then((response) => {
                this.picacodeReleases = response.data.results
            })

        // TODO: fetch pricelists if not passed as prop
    },

    computed: {
        hasPicacodeReleases() {
            if (this.picacodeReleases === null)
                return false
            return this.picacodeReleases.length > 0
        },
    },

    methods: {
        setPicacodeReleaseField(prId, field, value) {
            let fieldObj;
            if (this.picacodeReleaseFields[prId] !== undefined) {
                fieldObj = this.picacodeReleaseFields[prId]
                fieldObj[field] = value
            } else {
                fieldObj = {
                    [field]: value
                }
            }
            this.picacodeReleaseFields[prId] = fieldObj
        },

        isPicacodeReleasePriceListChanged(originalPicaCodeRelease, newId) {
            if (originalPicaCodeRelease.price_list === null) {
                return newId === null ? false : true
            } else {
                return originalPicaCodeRelease.price_list.id !== newId
            }
        },

        isPicacodeReleaseCodecChanged(originalPicaCodeRelease, newId) {
            if (originalPicaCodeRelease.codec === null) {
                return newId === null ? false : true
            } else {
                return originalPicaCodeRelease.codec.id !== newId
            }
        },

        isPicacodeReleaseChanged(originalPicaCodeRelease, payload) {
            if (payload['price_list_id'] !== undefined && this.isPicacodeReleasePriceListChanged(originalPicaCodeRelease, payload['price_list_id'])) {
                return true
            } else if (payload['codec'] !== undefined && this.isPicacodeReleaseCodecChanged(originalPicaCodeRelease, payload['codec'])) {
                return true
            } else {
                return false
            }
        },

        save() {
            // TODO: bulk update

            for (const [prId, fields] of Object.entries(this.picacodeReleaseFields)) {
                this.loading = true

                let originalPicaCodeReleaseIndex = this.picacodeReleases.findIndex(obj => {
                    return obj.id === parseInt(prId)
                })

                let originalPicaCodeRelease = this.picacodeReleases[originalPicaCodeReleaseIndex]

                let payload = {}

                if (fields['priceList'] !== undefined) {
                    payload['price_list_id'] = fields['priceList'] !== null ? fields['priceList'].id : null
                }

                if (fields['codec'] !== undefined) {
                    payload['codec'] = fields['codec'] !== null ? fields['codec'].id : null
                }

                // TODO: refactor isPicacodeReleaseChanged
                // const isPicacodeReleaseChanged = Object.entries(payload).filter((fieldName, fieldValue) =>
                //     originalPicaCodeRelease[fieldName] !== fieldValue
                // ).length > 0

                // console.log(isPicacodeReleaseChanged)

                if (this.isPicacodeReleaseChanged(originalPicaCodeRelease, payload)) {
                    PicacodeReleaseService.updatePicacodeRelease(prId, payload).then(() => {
                        // I update the fields with the values of the last save,
                        // otherwise I would always have the one loaded in the mounted

                        if (payload['price_list_id'] !== undefined) {

                            // update price list in state
                            if (payload['price_list_id'] === null) {
                                this.picacodeReleases[originalPicaCodeReleaseIndex].price_list = null
                            } else {
                                this.picacodeReleases[originalPicaCodeReleaseIndex].price_list = this.priceLists.find(obj => {
                                    return obj.id === payload['price_list_id']
                                })
                            }
                        }

                        if (payload['codec'] !== undefined) {

                            // update codec in state
                            if (payload['codec'] === null) {
                                this.picacodeReleases[originalPicaCodeReleaseIndex].codec = null
                            } else {
                                this.picacodeReleases[originalPicaCodeReleaseIndex].codec = this.codecs.find(obj => {
                                    return obj.id === payload['codec']
                                })
                            }
                        }

                        this.notifyMessage({
                            text: this.$gettext('Pica Code release fields set correctly'),
                            messageType: 'success'
                        })
                    }).finally(() => {
                        this.loading = false
                    })
                } else {
                    this.loading = false
                }


            }
        }
    }
}
</script>
